import { useRef, useState, useEffect } from "react";
import SignatureCanvas from "react-signature-canvas";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Eraser, Check, RotateCcw, PenTool, Save, Loader2 } from "lucide-react";

export default function SignaturePad({ 
  open, 
  onClose, 
  onSave, 
  existingSignature = null,
  title = "Signature électronique",
  description = "Dessinez votre signature dans le cadre ci-dessous"
}) {
  const sigCanvas = useRef(null);
  const [isEmpty, setIsEmpty] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && existingSignature && sigCanvas.current) {
      // Load existing signature
      setTimeout(() => {
        if (sigCanvas.current) {
          sigCanvas.current.fromDataURL(existingSignature);
          setIsEmpty(false);
        }
      }, 100);
    }
  }, [open, existingSignature]);

  const handleClear = () => {
    if (sigCanvas.current) {
      sigCanvas.current.clear();
      setIsEmpty(true);
    }
  };

  const handleEnd = () => {
    if (sigCanvas.current) {
      setIsEmpty(sigCanvas.current.isEmpty());
    }
  };

  const handleSave = async () => {
    if (sigCanvas.current && !sigCanvas.current.isEmpty()) {
      setSaving(true);
      try {
        // Get signature as PNG base64
        const signatureData = sigCanvas.current.getTrimmedCanvas().toDataURL("image/png");
        await onSave(signatureData);
        onClose();
      } catch (error) {
        console.error(/* PROD */ "Error saving signature:", error);
      } finally {
        setSaving(false);
      }
    }
  };

  const handleClose = () => {
    handleClear();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PenTool className="w-5 h-5 text-[#1B4D3E]" />
            {title}
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Signature Canvas */}
          <div className="border-2 border-dashed border-gray-300 rounded-lg bg-white relative">
            <SignatureCanvas
              ref={sigCanvas}
              canvasProps={{
                className: "w-full h-48 rounded-lg",
                style: { width: "100%", height: "200px" }
              }}
              backgroundColor="white"
              penColor="#1B4D3E"
              onEnd={handleEnd}
            />
            
            {/* Placeholder text when empty */}
            {isEmpty && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <p className="text-gray-400 text-sm">Dessinez votre signature ici</p>
              </div>
            )}
          </div>

          {/* Canvas Controls */}
          <div className="flex items-center justify-between">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleClear}
              disabled={isEmpty}
            >
              <Eraser className="w-4 h-4 mr-2" />
              Effacer
            </Button>
            
            <div className="text-xs text-gray-500">
              {isEmpty ? "Signature vide" : "Signature prête"}
            </div>
          </div>

          {/* Info box */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
            <p className="font-medium mb-1">⚠️ Information importante</p>
            <p>
              Cette signature sera utilisée sur tous les documents officiels générés par la plateforme 
              (quittances de bilans, décisions d'habilitation). Elle sera accompagnée d'un horodatage 
              et d'un certificat d'intégrité.
            </p>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleClose}>
            Annuler
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={isEmpty || saving}
            className="bg-[#1B4D3E] hover:bg-[#163d32]"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Enregistrer la signature
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Preview component for displaying saved signature
export function SignaturePreview({ signature, onEdit, showEditButton = true }) {
  if (!signature) {
    return (
      <div className="border-2 border-dashed border-gray-200 rounded-lg p-6 text-center">
        <PenTool className="w-8 h-8 mx-auto mb-2 text-gray-400" />
        <p className="text-sm text-gray-500">Aucune signature enregistrée</p>
        {showEditButton && (
          <Button variant="outline" size="sm" className="mt-3" onClick={onEdit}>
            Créer ma signature
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs text-gray-500 mb-2">Signature enregistrée</p>
          <div className="bg-white border rounded p-2 inline-block">
            <img 
              src={signature} 
              alt="Signature" 
              className="h-16 object-contain"
            />
          </div>
        </div>
        {showEditButton && (
          <Button variant="outline" size="sm" onClick={onEdit}>
            <RotateCcw className="w-4 h-4 mr-1" />
            Modifier
          </Button>
        )}
      </div>
    </div>
  );
}
